import { useContext, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AppContext } from '../../context/AppContext';
import { OrderConfirmationPaymentContext } from '../../context/OrderConfirmationPaymentContext';
import PayPalConnectModal from '../Modals/PayPalConnectModal';
import PayPalDisconnectModal from '../Modals/PayPalDisconnectModal';
import StripeConnectModal from '../Modals/StripeConnectModal';
import StripeDisconnectModal from '../Modals/StripeDisconnectModal';
import { STRIPE_FEES, PAYPAL_FEES } from '../../utils/constants';
import { validateRequired, validateEmail } from '../../utils/validation';
import stripeLogo from '../../assets/logos/stripe.svg';
import paypalLogo from '../../assets/logos/paypal.svg';

const OrderConfirmationEditPane = () => {
  const { state, updateBusinessInfo } = useContext(AppContext);
  const { paymentProviders, loading, refetchPaymentProviders } = useContext(OrderConfirmationPaymentContext);
  const [paypalModalOpen, setPaypalModalOpen] = useState(false);
  const [paypalDisconnectOpen, setPaypalDisconnectOpen] = useState(false);
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const [stripeDisconnectOpen, setStripeDisconnectOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const isStripeEnabled = Boolean(paymentProviders?.is_stripe_enabled);
  const isPayPalEnabled = Boolean(paymentProviders?.is_paypal_enabled);

  const handleFieldChange = (field, value) => {
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    updateBusinessInfo({ [field]: value });
  };

  const handleValidation = (field, value) => {
    let error = '';
    if (field === 'companyName') {
      error = validateRequired(value, 'Company name');
    } else if (field === 'email') {
      error = validateEmail(value);
    } else if (field === 'address') {
      error = validateRequired(value, 'Address');
    }
    setErrors({ ...errors, [field]: error });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, minWidth: { xs: 0, sm: 400 }, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#23272E' }}>
        YOUR BUSINESS INFORMATION
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This information will be used on the order confirmation
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
        <TextField
          label="Company name"
          value={state.businessInfo.companyName}
          onChange={(e) => handleFieldChange('companyName', e.target.value)}
          onBlur={(e) => handleValidation('companyName', e.target.value)}
          error={!!errors.companyName}
          helperText={errors.companyName}
          placeholder="<name_from_the_domain_registrant>"
          fullWidth
          required
        />

        <TextField
          label="Email address"
          type="email"
          value={state.businessInfo.email}
          onChange={(e) => handleFieldChange('email', e.target.value)}
          onBlur={(e) => handleValidation('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          placeholder="<email_from_domain_registrant_details>"
          fullWidth
          required
        />

        <TextField
          label="Address"
          value={state.businessInfo.address}
          onChange={(e) => handleFieldChange('address', e.target.value)}
          onBlur={(e) => handleValidation('address', e.target.value)}
          error={!!errors.address}
          helperText={errors.address}
          placeholder="<address_from_domain_registrant_details>"
          fullWidth
          multiline
          rows={3}
          required
        />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        Payment provider
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Stripe Card */}
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <img src={stripeLogo} alt="Stripe Logo" style={{ height: 30 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Processing fees: {STRIPE_FEES.PERCENTAGE}% + {Math.round(STRIPE_FEES.FIXED * 100)} cent per transaction
                </Typography>
              </Box>
              {!isStripeEnabled && (
                <Button
                  variant="outlined"
                  onClick={() => setStripeModalOpen(true)}
                  sx={{
                    borderColor: '#326EEA',
                    color: '#326EEA',
                    textTransform: 'none',
                    fontWeight: 500,
                    marginTop: '12px',
                    marginLeft: '10px',
                  }}
                >
                  Connect
                </Button>
              )}
            </Box>
            {isStripeEnabled && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#326EEA', fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Connected
                    </Typography>
                  </Box>
                  <Typography
                    onClick={() => setStripeDisconnectOpen(true)}
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Disconnect
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* PayPal Card */}
        <Card sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <img src={paypalLogo} alt="PayPal Logo" style={{ height: 30 }} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Processing fees: {PAYPAL_FEES.PERCENTAGE}% + {Math.round(PAYPAL_FEES.FIXED * 100)} cent per transaction
                </Typography>
              </Box>
              {!isPayPalEnabled && (
                <Button
                  variant="outlined"
                  onClick={() => setPaypalModalOpen(true)}
                  sx={{
                    borderColor: '#326EEA',
                    color: '#326EEA',
                    textTransform: 'none',
                    fontWeight: 500,
                    marginTop: '12px',
                    marginLeft: '10px',
                  }}
                >
                  Connect
                </Button>
              )}
            </Box>
            {isPayPalEnabled && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#326EEA', fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      Connected
                    </Typography>
                  </Box>
                  <Typography
                    onClick={() => setPaypalDisconnectOpen(true)}
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Disconnect
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
      )}

      <PayPalConnectModal
        open={paypalModalOpen}
        onClose={() => setPaypalModalOpen(false)}
        onSuccess={() => refetchPaymentProviders()}
      />
      <PayPalDisconnectModal
        open={paypalDisconnectOpen}
        onClose={() => setPaypalDisconnectOpen(false)}
        onSuccess={() => refetchPaymentProviders()}
      />
      <StripeConnectModal
        open={stripeModalOpen}
        onClose={() => setStripeModalOpen(false)}
        onSuccess={() => refetchPaymentProviders()}
      />
      <StripeDisconnectModal
        open={stripeDisconnectOpen}
        onClose={() => setStripeDisconnectOpen(false)}
        onSuccess={() => refetchPaymentProviders()}
      />
    </Box>
  );
};

export default OrderConfirmationEditPane;
